import {SelectButton} from "primereact/selectbutton";
import {useSelector} from "react-redux";

import {ALL_SOURCES} from "@reva/shared/parameter-resolution";

import {selectAssociatedOutputParamOutputCurried} from "../../redux/objectParametersSelectors";
import ParameterFieldContainer from "./ParameterFieldContainer";
import {getResourceColor} from "./paramHooks";

/**
 * Shared SelectButton rendering for parameter inputs whose values map to a fixed set of buttons
 * (categories, or #18858 value ranges): conflict coloring, associated-output highlighting and the
 * SelectButton itself are identical between them - only how a raw value resolves to an option's
 * `value` differs (identity for categories, range lookup for ranges), via `resolveOptionValue`.
 * @param {string} parameter parameter key
 * @param {RevaValueParameterType} parameterType type of parameter e.g. "AVM"
 * @param {string|null} [label] rendered label
 * @param {string} id ID of the input
 * @param {InputParameterDetailsDef|null} [parameter_details_def] optional parameter details definition
 * @param {{value: *, label: string, valueLabel?: string, group?: string}[]} options button options; `valueLabel`
 *   replaces `label` wherever the value is shown outside the buttons, options with a `group` are rendered in one row
 *   per group (single selection only)
 * @param {boolean} [multiple] allow selecting multiple options
 * @param {string|null} [associatedOutputParam] optional output parameter name, to highlight its option as a default
 * @param {number|null} [associatedOutputParamValue] optional default value to highlight directly, as an alternative to `associatedOutputParam` for callers whose suggestion isn't an output parameter (e.g. location_flood_risk's climate-risk suggestion)
 * @param {(rawValue: *) => *} resolveOptionValue maps a raw parameter value to the option value it corresponds to
 * @param {string|null} [extraTooltipText] optional extra text appended to the label tooltip
 * @return {React.JSX.Element}
 */
export default function ParameterInputSelectButtons({
    parameter,
    parameterType,
    label,
    id,
    parameter_details_def,
    options,
    multiple,
    associatedOutputParam,
    associatedOutputParamValue: associatedOutputParamValueProp,
    resolveOptionValue,
    extraTooltipText,
}) {
    const associatedOutputParamValueFromSelector = useSelector(
        selectAssociatedOutputParamOutputCurried(associatedOutputParam),
    );
    const associatedOutputParamValue = associatedOutputParam
        ? associatedOutputParamValueFromSelector
        : associatedOutputParamValueProp;
    const valueLabels = options.reduce(
        (acc, {value, label, valueLabel}) => ({...acc, [value]: valueLabel ?? label}),
        {},
    );
    const groups = new Map();
    for (const option of options) {
        groups.set(option.group ?? "", [...(groups.get(option.group ?? "") ?? []), option]);
    }

    return (
        <ParameterFieldContainer
            id={id}
            label={label}
            parameter={parameter}
            parameterType={parameterType}
            parameter_details_def={parameter_details_def}
            showDescription={true}
            valueLabels={valueLabels}
            hasAssociatedOutputParam={associatedOutputParamValue != null}
            showAssociatedOutputParamHint={false}
            associatedOutputParamValue={associatedOutputParamValue}
            extraTooltipText={extraTooltipText}
            renderControl={({className, onChange, value, activeParameterType, hasConflict, disabled, allValues}) => {
                const resolvedValue = resolveOptionValue(value);
                const associatedResolvedValue = resolveOptionValue(associatedOutputParamValue);
                // DOC_PROPERTY (z.B. location_flood_risk) hat kein Gegenstück in ALL_SOURCES,
                // resolveActiveParameterValue liefert dafür nie ein activeType (siehe #19115) - der
                // gesetzte Wert ist dann trotzdem "manuell", daher wie reva einfärben.
                const sourceColor = getResourceColor(
                    parameterType === "DOC_PROPERTY" ? "reva" : activeParameterType,
                    hasConflict,
                    activeParameterType === "immodrop",
                );
                const optionColors =
                    hasConflict &&
                    ALL_SOURCES.toReversed()
                        .filter((source) => allValues[source] != null)
                        .reduce(
                            (object, source) => ({
                                [resolveOptionValue(allValues[source])]: getResourceColor(source),
                                ...object,
                            }),
                            {},
                        );

                const getButtonClassName = (context) => {
                    const optionColor = optionColors[context.option.value];
                    const coloredBorderAndText = `border-${sourceColor} ${optionColor ? `text-${optionColor}` : ""}`;
                    const coloredBorderAndBackground = `${context.selected ? `bg-${sourceColor} border-${sourceColor}` : ""}`;
                    const isAssociatedOutput =
                        !hasConflict && value == null && context.option.value === associatedResolvedValue;
                    return (
                        `p-button-sm ${className} ${isAssociatedOutput ? "bg-gray-200" : ""} ` +
                        (hasConflict ? coloredBorderAndText : coloredBorderAndBackground)
                    );
                };

                const renderSelectButton = (buttonOptions, props) => (
                    <SelectButton
                        value={resolvedValue}
                        disabled={disabled}
                        pt={{button: ({context}) => ({className: getButtonClassName(context)})}}
                        options={buttonOptions}
                        onChange={onChange}
                        multiple={multiple}
                        data-pr-position="top"
                        {...props}
                    />
                );

                if (multiple || (groups.size === 1 && groups.has(""))) {
                    return renderSelectButton(options, {id, className: `w-full ${parameter}`});
                }

                // Die Überschrift benennt ihre Button-Gruppe (aria-labelledby), denn der Gruppenname allein ist nicht
                // eindeutig: er kann zugleich Kurzname einer Kategorie sein (#19431)
                return (
                    <div id={id} className={`w-full flex flex-col gap-2 ${parameter}`}>
                        {[...groups].map(([group, groupOptions], index) => {
                            const headingId = group ? `${id}-group-${index}` : undefined;
                            return (
                                <div key={group}>
                                    {group && (
                                        <div
                                            id={headingId}
                                            className="text-xs font-semibold uppercase text-muted-color mb-1"
                                        >
                                            {group}
                                        </div>
                                    )}
                                    {renderSelectButton(groupOptions, {
                                        className: "w-full",
                                        "aria-labelledby": headingId,
                                    })}
                                </div>
                            );
                        })}
                    </div>
                );
            }}
        />
    );
}
